import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";

export interface TranscriptChoiceObject {
  text: string;
  correct: boolean;
}

export interface TranscriptPayload {
  attempt_id: string;
  assignment_id: string;
  recipient_email: string;
  passed: boolean;
  score: number | null;
  completed_at: string;
  questions: Array<{
    question_id: string;
    stem: string;
    choices: TranscriptChoiceObject[];
    selected_index?: number | null;
    selected_indices?: number[];
    answered_correctly: boolean;
  }>;
}

/** Path for the organizer full-page transcript (**SPA**). */
export function organizerTranscriptPageHref(
  quizId: string,
  assignmentId: string,
): string {
  return `/quizzes/view/${encodeURIComponent(quizId)}/transcript/${encodeURIComponent(assignmentId)}`;
}

export interface ParticipantTranscriptReviewRow {
  question_id: string;
  stem: string;
  choice_labels: string[];
  selected_indices: number[];
  answered_correctly: boolean;
}

export function isTranscriptPayload(data: unknown): data is TranscriptPayload {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const o = data as Record<string, unknown>;
  if (typeof o.attempt_id !== "string" || typeof o.assignment_id !== "string") {
    return false;
  }
  if (typeof o.recipient_email !== "string") {
    return false;
  }
  if (typeof o.passed !== "boolean") {
    return false;
  }
  if (o.score !== null && typeof o.score !== "number") {
    return false;
  }
  if (typeof o.completed_at !== "string") {
    return false;
  }
  if (!Array.isArray(o.questions)) {
    return false;
  }
  for (const q of o.questions) {
    if (typeof q !== "object" || q === null) {
      return false;
    }
    const qr = q as Record<string, unknown>;
    if (typeof qr.question_id !== "string" || typeof qr.stem !== "string") {
      return false;
    }
    if (!Array.isArray(qr.choices)) {
      return false;
    }
    for (const c of qr.choices) {
      if (typeof c !== "object" || c === null) {
        return false;
      }
      const co = c as Record<string, unknown>;
      if (typeof co.text !== "string" || typeof co.correct !== "boolean") {
        return false;
      }
    }
    const si = qr.selected_index;
    if (si !== null && si !== undefined && typeof si !== "number") {
      return false;
    }
    const sis = qr.selected_indices;
    if (
      sis !== undefined &&
      (!Array.isArray(sis) || !sis.every((c) => typeof c === "number"))
    ) {
      return false;
    }
    if (typeof qr.answered_correctly !== "boolean") {
      return false;
    }
  }
  return true;
}

function choicesAt(choices: string[], indices: number[]): string {
  const parts = indices
    .filter((i) => Number.isInteger(i) && i >= 0 && i < choices.length)
    .map((i) => choices[i] ?? "");
  return parts.filter((s) => s.trim() !== "").join("; ");
}

function correctIndicesFromTranscriptChoices(
  choices: TranscriptChoiceObject[],
): number[] {
  return choices.map((c, i) => (c.correct ? i : -1)).filter((i) => i >= 0);
}

function formatTranscriptDetailDate(
  iso: string,
  locale: SupportedLocale,
): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return iso;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

/**
 * Renders the numbered question rundown used in organizer dialogs (full keys).
 *
 * @param mount - Parent node (child list is appended).
 */
export function renderTranscriptQuestionList(
  mount: HTMLElement,
  t: Translate,
  locale: SupportedLocale,
  questions: TranscriptPayload["questions"],
): void {
  const ol = document.createElement("ol");
  ol.className = "quiz-detail__transcript-questions";
  for (const q of questions) {
    const li = document.createElement("li");
    const stem = document.createElement("p");
    stem.className = "quiz-detail__transcript-q-stem";
    stem.textContent = q.stem;
    li.appendChild(stem);
    const labels = q.choices.map((c) => c.text);
    const selIdxs =
      Array.isArray(q.selected_indices) && q.selected_indices.length > 0
        ? q.selected_indices
        : q.selected_index !== null && q.selected_index !== undefined
          ? [q.selected_index]
          : [];
    const selText = choicesAt(labels, selIdxs);
    const selP = document.createElement("p");
    selP.className = "quiz-detail__transcript-answer";
    selP.textContent =
      selText.trim() !== ""
        ? t("quiz.detail.transcript_selected_option", { text: selText })
        : t("quiz.detail.transcript_no_selection");
    li.appendChild(selP);
    const corIdxs = correctIndicesFromTranscriptChoices(q.choices);
    const corText = choicesAt(labels, corIdxs);
    const corP = document.createElement("p");
    corP.className = "quiz-detail__transcript-answer";
    corP.textContent = t("quiz.detail.transcript_correct_option", {
      text: corText,
    });
    li.appendChild(corP);
    const mark = document.createElement("p");
    mark.className = q.answered_correctly
      ? "quiz-detail__transcript-mark quiz-detail__transcript-mark--ok"
      : "quiz-detail__transcript-mark quiz-detail__transcript-mark--bad";
    mark.textContent = q.answered_correctly
      ? t("quiz.detail.transcript_mark_correct")
      : t("quiz.detail.transcript_mark_incorrect");
    li.appendChild(mark);
    ol.appendChild(li);
  }
  mount.appendChild(ol);
}

/**
 * Participant results when the organizer-style transcript (correct answers and mark per item)
 * cannot be loaded — shows stems and learner selections only.
 *
 * @param mount - Append-only parent.
 */
export function renderParticipantTranscriptSelectionOnly(
  mount: HTMLElement,
  t: Translate,
  rows: Array<{
    stem: string;
    choice_labels: string[];
    selected_indices: number[];
  }>,
): void {
  const ol = document.createElement("ol");
  ol.className = "quiz-detail__transcript-questions";
  for (const q of rows) {
    const li = document.createElement("li");
    const stem = document.createElement("p");
    stem.className = "quiz-detail__transcript-q-stem";
    stem.textContent = q.stem;
    li.appendChild(stem);
    const selText = choicesAt(q.choice_labels, q.selected_indices);
    const selP = document.createElement("p");
    selP.className = "quiz-detail__transcript-answer";
    selP.textContent =
      selText.trim() !== ""
        ? t("quiz.detail.transcript_selected_option", { text: selText })
        : t("quiz.detail.transcript_no_selection");
    li.appendChild(selP);
    ol.appendChild(li);
  }
  mount.appendChild(ol);
}

/**
 * Participant results review: every option with selection checkboxes, then **Correct** / **Wrong**.
 *
 * @param mount - Append-only parent.
 */
export function renderParticipantTranscriptReview(
  mount: HTMLElement,
  t: Translate,
  rows: ParticipantTranscriptReviewRow[],
): void {
  const ol = document.createElement("ol");
  ol.className = "quiz-detail__transcript-questions";
  for (const q of rows) {
    const li = document.createElement("li");
    const stem = document.createElement("p");
    stem.className = "quiz-detail__transcript-q-stem";
    stem.textContent = q.stem;
    li.appendChild(stem);
    const picked = new Set(q.selected_indices);
    const ul = document.createElement("ul");
    ul.className = "quiz-detail__transcript-choice-list";
    for (let i = 0; i < q.choice_labels.length; i++) {
      const text = q.choice_labels[i] ?? "";
      const row = document.createElement("li");
      row.className = "quiz-detail__transcript-choice-row";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.disabled = true;
      cb.checked = picked.has(i);
      cb.setAttribute(
        "aria-label",
        t("quiz.participant.transcript_option_aria", {
          n: String(i + 1),
          text,
        }),
      );
      const span = document.createElement("span");
      span.className = "quiz-detail__transcript-choice-text";
      span.textContent = text;
      row.appendChild(cb);
      row.appendChild(span);
      ul.appendChild(row);
    }
    li.appendChild(ul);
    const mark = document.createElement("p");
    const ok = q.answered_correctly;
    mark.className = ok
      ? "quiz-detail__transcript-mark quiz-detail__transcript-mark--ok participant-quiz__transcript-verdict participant-quiz__transcript-verdict--ok"
      : "quiz-detail__transcript-mark quiz-detail__transcript-mark--bad participant-quiz__transcript-verdict participant-quiz__transcript-verdict--bad";
    mark.textContent = ok
      ? t("quiz.participant.transcript_question_correct")
      : t("quiz.participant.transcript_question_wrong");
    li.appendChild(mark);
    ol.appendChild(li);
  }
  mount.appendChild(ol);
}

/** Reserved blank space at the end of the organizer transcript for signatures when printed. */
export function appendTranscriptSigningArea(
  mount: HTMLElement,
  t: Translate,
): void {
  const box = document.createElement("div");
  box.className = "quiz-detail__transcript-signing-area";
  box.setAttribute("role", "region");
  box.setAttribute("aria-label", t("quiz.detail.transcript_signing_area_aria"));
  mount.appendChild(box);
}

export function renderTranscriptContent(
  mount: HTMLElement,
  t: Translate,
  locale: SupportedLocale,
  payload: TranscriptPayload,
  recipientEmail: string,
): void {
  mount.replaceChildren();
  const meta = document.createElement("p");
  meta.className = "quiz-detail__transcript-meta";
  meta.textContent = t("quiz.detail.transcript_meta_line", {
    email: recipientEmail,
    date: formatTranscriptDetailDate(payload.completed_at, locale),
  });
  mount.appendChild(meta);
  const aid = document.createElement("p");
  aid.className = "quiz-detail__muted";
  aid.textContent = t("quiz.detail.transcript_attempt_id", {
    id: payload.attempt_id,
  });
  mount.appendChild(aid);
  const out = document.createElement("p");
  out.className = "quiz-detail__transcript-outcome";
  out.textContent = payload.passed
    ? t("quiz.detail.transcript_outcome_passed")
    : t("quiz.detail.transcript_outcome_failed");
  mount.appendChild(out);
  if (typeof payload.score === "number" && Number.isFinite(payload.score)) {
    const pct = Math.round(payload.score * 1000) / 10;
    const sc = document.createElement("p");
    sc.className = "quiz-detail__muted";
    sc.textContent = t("quiz.detail.transcript_score", {
      percent: String(pct),
    });
    mount.appendChild(sc);
  }
  renderTranscriptQuestionList(mount, t, locale, payload.questions);
  appendTranscriptSigningArea(mount, t);
}
